/**
 * SplitText — global split-text hover animation manager. Finds elements with
 * `[data-hover="split-text"]`, splits their text content into per-letter masks
 * so CSS can animate each letter independently on hover (staggered translateY
 * with ::after duplicate technique).
 *
 * Hover state is toggled via JS class `.is-hovering` (mouseenter/mouseleave)
 * instead of CSS :hover, for reliable hover-in/hover-out across all contexts
 * (buttons, links, nested elements). Direct addEventListener is used because
 * mouseenter/mouseleave do not bubble (Delegate is not applicable).
 *
 * MutationObserver (rAF-throttled) re-scans after Shopify Section Rendering
 * API DOM swaps, so dynamically added elements are picked up automatically.
 *
 * Loaded as ES module via `@theme/split-text` importmap entry.
 * Exports both the class and a pre-built `splitText` singleton.
 * Module is loaded globally from `layout/theme.liquid` so the singleton
 * registers its document-level observer on every page.
 *
 * Dependencies: none
 */

export class SplitText {
  constructor() {
    this.processed = new WeakSet();
    this.observer = null;
    this.rafId = 0;

    this.processAll();
    this.bindEvents();
  }

  bindEvents() {
    this.observer = new MutationObserver(this.handleMutations.bind(this));
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  handleMutations(mutations) {
    const hasAddedNodes = mutations.some((m) => m.addedNodes.length > 0);
    if (!hasAddedNodes) return;
    this.scheduleProcess();
  }

  scheduleProcess() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0;
      this.processAll();
    });
  }

  processAll() {
    const els = document.querySelectorAll(
      '[data-hover="split-text"]:not([data-split-ready])',
    );
    for (const el of els) this.split(el);
  }

  /**
   * Split a single element's text content into per-letter mask spans
   * and bind hover listeners. Safe to call on an already-processed
   * element (no-op).
   *
   * @param {HTMLElement} el
   */
  split(el) {
    if (this.processed.has(el)) return;
    this.processed.add(el);

    const text = el.textContent.trim();
    if (!text) return;

    if (!el.getAttribute('aria-label')) {
      el.setAttribute('aria-label', text);
    }
    el.dataset.splitReady = '';

    const frag = document.createDocumentFragment();
    const words = text.split(/(\s+)/);
    let i = 0;

    for (const segment of words) {
      if (/^\s+$/.test(segment)) {
        const space = document.createElement('span');
        space.className = 'split-word-space';
        space.textContent = '\u00A0';
        frag.appendChild(space);
        continue;
      }

      const word = document.createElement('span');
      word.className = 'split-word';
      word.setAttribute('aria-hidden', 'true');

      for (const char of segment) {
        const mask = document.createElement('span');
        mask.className = 'split-letter-mask';

        const letter = document.createElement('span');
        letter.className = 'split-letter';
        letter.textContent = char;
        letter.dataset.char = char;
        letter.style.setProperty('--letter-index', i++);

        mask.appendChild(letter);
        word.appendChild(mask);
      }

      frag.appendChild(word);
    }

    el.textContent = '';
    el.appendChild(frag);

    this.bindHover(el);
  }

  /**
   * Bind mouseenter/mouseleave on the hover target. Refs stored on
   * the element itself (same pattern as Popup._overlayClickHandler)
   * so removed DOM nodes don't leak in a long-lived Map.
   *
   * @param {HTMLElement} el  The [data-hover="split-text"] element
   */
  bindHover(el) {
    const hoverTarget = el.closest('.Button') || el;

    el._splitEnter = () => el.classList.add('is-hovering');
    el._splitLeave = () => el.classList.remove('is-hovering');
    el._splitTarget = hoverTarget;

    hoverTarget.addEventListener('mouseenter', el._splitEnter);
    hoverTarget.addEventListener('mouseleave', el._splitLeave);
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    document.querySelectorAll('[data-split-ready]').forEach((el) => {
      if (el._splitTarget) {
        el._splitTarget.removeEventListener('mouseenter', el._splitEnter);
        el._splitTarget.removeEventListener('mouseleave', el._splitLeave);
        delete el._splitTarget;
        delete el._splitEnter;
        delete el._splitLeave;
      }
    });
  }
}

export const splitText = new SplitText();
window.splitText = splitText;
